import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { API } from "../../lib/api";
import { ArrowLeft, Trash, Plus, UploadSimple } from "@phosphor-icons/react";
import MediaBlocksEditor from "../../components/admin/MediaBlocksEditor";

const empty = {
  slug: "", title: "", tagline: "", short_description: "", full_description: "",
  hero_image: "", hero_video_url: "", intro_video_url: "",
  gallery: [], media_blocks: [], meeting_location: "", map_pin_url: "", duration_minutes: 120,
  duration_label: "2 hours",
  walking_distance_km: 1.5, difficulty: "Easy", included_food: "", what_to_bring: "",
  custom_label_1: "", custom_value_1: "",
  custom_label_2: "", custom_value_2: "",
  cancellation_policy: "", drinks_disclaimer: "Drinks are purchased separately and are not included in the ticket price.",
  confirmation_briefing: "",
  faq: [], base_price: 95, min_group_size: 1, small_group_surcharge: 0,
  min_guests: 2, max_guests: 12,
  is_active: true, seo_meta_description: "",
};

const DURATION_OPTIONS = ["2 hours", "3 hours", "3-4 hours", "4 hours", "5-6 hours", "6 hours"];

export default function AdminTourEdit() {
  const { id } = useParams();
  const isNew = id === "new";
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);
  const galRef = useRef(null);

  useEffect(() => {
    if (!isNew) api.get(`/tours/${id}`).then(r => setForm({ ...empty, ...r.data })).catch(() => {});
  }, [id, isNew]);

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    const r = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data.url; // relative path like "/api/files/..." — works on any domain
  };
  const onHero = async (e) => { const f = e.target.files?.[0]; if (!f) return; upd("hero_image", await upload(f)); };
  const onGallery = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    const urls = await Promise.all(files.map(upload));
    upd("gallery", [...(form.gallery || []), ...urls]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      if (isNew) {
        const r = await api.post("/tours", form);
        nav(`/admin/tours/${r.data.id}`);
      } else {
        await api.patch(`/tours/${id}`, form);
      }
    } catch (err) { setError(err.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-10 md:p-12 max-w-4xl" data-testid="admin-tour-edit">
      <button onClick={() => nav(-1)} className="flex items-center gap-2 text-ms-stone hover:text-ms-earth text-sm"><ArrowLeft size={16} /> Back</button>
      <div className="flex items-baseline justify-between flex-wrap gap-3 mt-4">
        <h1 className="serif text-4xl text-ms-earth">{isNew ? "New tour" : "Edit tour"}</h1>
        {!isNew && (
          <Link to={`/admin/calendar?tour_id=${id}`} className="inline-flex items-center gap-2 text-xs text-ms-brass hover:text-ms-brass-lo border-b border-ms-brass pb-0.5 label-eyebrow" data-testid="manage-dates-link">
            Manage dates →
          </Link>
        )}
      </div>

      <form onSubmit={submit} className="mt-8 space-y-6 bg-white border border-ms-edge rounded-2xl p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Input label="Title" value={form.title} onChange={v=>upd("title",v)} required testId="tour-title" emailHint />
          <Input label="Slug (URL)" value={form.slug} onChange={v=>upd("slug",v.toLowerCase().replace(/[^a-z0-9-]/g,"-"))} required testId="tour-slug" />
          <Input label="Tagline" value={form.tagline} onChange={v=>upd("tagline",v)} testId="tour-tagline" />
          <Input label="Difficulty" value={form.difficulty} onChange={v=>upd("difficulty",v)} testId="tour-difficulty" />
          <Input label="Base price ($)" type="number" value={form.base_price} onChange={v=>upd("base_price", Number(v))} testId="tour-price" />
          <label>
            <span className="label-eyebrow text-ms-stone text-xs">Duration</span>
            <input
              list="duration-options"
              value={form.duration_label || ""}
              onChange={e=>upd("duration_label", e.target.value)}
              placeholder="e.g. 2 hours, 3-4 hours"
              data-testid="tour-duration"
              className="mt-2 w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm focus:border-ms-brass outline-none"
            />
            <datalist id="duration-options">
              {DURATION_OPTIONS.map(o => <option key={o} value={o} />)}
            </datalist>
          </label>
          <Input label="Walking (km)" type="number" step="0.1" value={form.walking_distance_km} onChange={v=>upd("walking_distance_km", Number(v))} />
          <Input label="Min group size" type="number" value={form.min_group_size} onChange={v=>upd("min_group_size", Number(v))} />
          <Input label="Small group surcharge ($)" type="number" value={form.small_group_surcharge} onChange={v=>upd("small_group_surcharge", Number(v))} />
          <Input label="Min guests (booking)" type="number" value={form.min_guests} onChange={v=>upd("min_guests", Number(v))} testId="tour-min-guests" />
          <Input label="Max guests (booking)" type="number" value={form.max_guests} onChange={v=>upd("max_guests", Number(v))} testId="tour-max-guests" />
          <Input label="Meeting location" value={form.meeting_location} onChange={v=>upd("meeting_location",v)} emailHint />
          <Input label="Google Maps pin URL" value={form.map_pin_url} onChange={v=>upd("map_pin_url",v)} placeholder="https://maps.google.com/?q=..." emailHint testId="tour-map-pin" />
        </div>

        <div className="bg-ms-cream/60 border border-ms-edge rounded-md p-5">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={!!form.split_pay_enabled} onChange={e=>upd("split_pay_enabled", e.target.checked)} className="mt-1 accent-ms-brass" data-testid="tour-split-pay" />
            <div>
              <p className="text-ms-ink text-sm font-medium">Allow split payment</p>
              <p className="text-ms-stone text-xs mt-1 leading-[1.6]">When enabled, the lead booker can pay just one share to lock the date. Each remaining guest receives a private payment link by email to pay their share. Booking auto-confirms when all shares are received.</p>
            </div>
          </label>
        </div>

        <Field label="Hero image">
          <div className="flex items-center gap-4">
            {form.hero_image && <img src={form.hero_image} className="w-24 h-24 object-cover rounded-md" alt="" />}
            <button type="button" onClick={() => fileRef.current?.click()} className="px-4 py-2 border border-ms-edge rounded-md text-sm hover:bg-ms-cream flex items-center gap-2"><UploadSimple size={14} /> Upload</button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onHero} data-testid="tour-hero-upload" />
            <input value={form.hero_image} onChange={e=>upd("hero_image",e.target.value)} placeholder="…or paste URL" className="flex-1 border border-ms-edge rounded-md px-3 py-2 text-sm" />
          </div>
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Input label="Hero video URL (optional)" value={form.hero_video_url} onChange={v=>upd("hero_video_url",v)} testId="tour-hero-video" />
          <Input label="Intro / trailer video URL (optional)" value={form.intro_video_url} onChange={v=>upd("intro_video_url",v)} testId="tour-intro-video" />
        </div>
        <p className="text-xs text-ms-stone -mt-3">Paste a YouTube, Vimeo, or direct .mp4 URL. When present, hero video plays in the background of the tour page hero.</p>

        <TextArea label="Short description" value={form.short_description} onChange={v=>upd("short_description",v)} rows={2} />
        <TextArea label="Full description" value={form.full_description} onChange={v=>upd("full_description",v)} rows={5} />
        <TextArea label="Included food" value={form.included_food} onChange={v=>upd("included_food",v)} rows={3} />
        <TextArea label="What to bring" value={form.what_to_bring} onChange={v=>upd("what_to_bring",v)} rows={2} />

        {/* Two rename-able custom fields. Hidden on the live page when value is empty. */}
        <div className="bg-ms-cream/60 border border-ms-edge rounded-md p-5 space-y-4">
          <p className="label-eyebrow text-ms-stone text-[10px]">Custom fields (optional — left empty stays hidden on the live page)</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label="Custom field 1 — label" value={form.custom_label_1} onChange={v=>upd("custom_label_1", v)} testId="tour-custom-label-1" />
            <div className="md:col-span-2">
              <Input label="Custom field 1 — value (short)" value={form.custom_value_1} onChange={v=>upd("custom_value_1", v)} testId="tour-custom-value-1" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label="Custom field 2 — label" value={form.custom_label_2} onChange={v=>upd("custom_label_2", v)} testId="tour-custom-label-2" />
            <div className="md:col-span-2">
              <TextArea label="Custom field 2 — value (long)" value={form.custom_value_2} onChange={v=>upd("custom_value_2", v)} rows={3} />
            </div>
          </div>
        </div>

        <TextArea label="Cancellation policy" value={form.cancellation_policy} onChange={v=>upd("cancellation_policy",v)} rows={3} />
        <TextArea label="Confirmation email briefing (sent after booking — supports **bold**, blank lines for paragraphs, and {first_name} {guest_count} {date} {booking_id} placeholders)" value={form.confirmation_briefing} onChange={v=>upd("confirmation_briefing",v)} rows={14} emailHint />
        <TextArea label="Drinks disclaimer" value={form.drinks_disclaimer} onChange={v=>upd("drinks_disclaimer",v)} rows={2} />

        <Field label="Gallery">
          <div className="flex flex-wrap gap-3">
            {(form.gallery || []).map((g, i) => (
              <div key={i} className="relative group">
                <img src={g} alt="" className="w-24 h-24 object-cover rounded-md" />
                <button type="button" onClick={() => upd("gallery", form.gallery.filter((_,j)=>j!==i))} className="absolute -top-2 -right-2 bg-ms-danger text-white rounded-full w-6 h-6 flex items-center justify-center"><Trash size={12} /></button>
              </div>
            ))}
            <button type="button" onClick={() => galRef.current?.click()} className="w-24 h-24 border-2 border-dashed border-ms-edge rounded-md flex items-center justify-center text-ms-stone hover:border-ms-brass"><Plus /></button>
            <input ref={galRef} type="file" accept="image/*" multiple hidden onChange={onGallery} />
          </div>
        </Field>

        <Field label="Page content (drag-to-reorder block builder)">
          <MediaBlocksEditor
            blocks={form.media_blocks}
            onChange={(updater) => setForm(f => ({
              ...f,
              media_blocks: typeof updater === "function"
                ? updater(Array.isArray(f.media_blocks) ? f.media_blocks : [])
                : updater,
            }))}
          />
        </Field>

        <Field label="Restaurant stops (for payout reports)">
          <RestaurantStopsEditor
            stops={form.restaurant_stops}
            onChange={(updater) => setForm(f => ({
              ...f,
              restaurant_stops: typeof updater === "function"
                ? updater(Array.isArray(f.restaurant_stops) ? f.restaurant_stops : [])
                : updater,
            }))}
          />
        </Field>

        <div className="bg-ms-cream/60 border border-ms-edge rounded-md p-5">
          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={form.review_capture_enabled !== false} onChange={e=>upd("review_capture_enabled", e.target.checked)} className="mt-1 accent-ms-brass" data-testid="tour-review-capture" />
            <div>
              <p className="text-ms-ink text-sm font-medium">Send automated review-request email after this tour</p>
              <p className="text-ms-stone text-xs mt-1 leading-[1.6]">Sends 24 hours after the tour ends, inviting the lead booker to leave a Google review. Disable for private events.</p>
            </div>
          </label>
        </div>

        <Field label="FAQ">
          <p className="text-xs text-ms-stone -mt-1 mb-3 leading-relaxed">
            Add common questions guests ask (e.g. "What if it rains?", "Is parking available?", "Can I bring kids?"). They appear as an expandable Q&A list near the bottom of the public tour page. Leave empty to hide the section.
          </p>
          <div className="space-y-4">
            {(form.faq || []).map((q, i) => (
              <div key={i} className="bg-ms-cream/50 border border-ms-edge rounded-md p-4 space-y-2" data-testid={`faq-item-${i}`}>
                <div className="flex items-center justify-between">
                  <p className="label-eyebrow text-ms-stone text-[10px]">Question {i + 1}</p>
                  <button type="button" onClick={() => upd("faq", form.faq.filter((_,j) => j!==i))} className="text-ms-danger hover:text-ms-danger/70 text-xs inline-flex items-center gap-1" data-testid={`faq-remove-${i}`}>
                    <Trash size={14} /> Remove
                  </button>
                </div>
                <input
                  value={q.question}
                  onChange={e => upd("faq", form.faq.map((x,j) => j===i ? {...x, question: e.target.value}: x))}
                  placeholder="What if it rains?"
                  spellCheck="true"
                  className="w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm focus:border-ms-brass outline-none font-medium"
                  data-testid={`faq-question-${i}`}
                />
                <textarea
                  value={q.answer}
                  onChange={e => upd("faq", form.faq.map((x,j) => j===i ? {...x, answer: e.target.value}: x))}
                  placeholder="The tour runs rain or shine — we'll provide ponchos. Severe weather only is rescheduled (you'll hear from us by 12pm day-of)."
                  rows={3}
                  spellCheck="true"
                  className="w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm focus:border-ms-brass outline-none leading-relaxed"
                  data-testid={`faq-answer-${i}`}
                />
              </div>
            ))}
            <button type="button" onClick={() => upd("faq", [...(form.faq||[]), {question:"", answer:""}])} className="btn-secondary" data-testid="faq-add-btn">
              <Plus size={14} /> Add a question
            </button>
          </div>
        </Field>

        <label className="flex items-center gap-3"><input type="checkbox" checked={form.is_active} onChange={e=>upd("is_active",e.target.checked)} className="accent-ms-brass" /> <span className="text-sm">Visible on public site</span></label>

        {error && <p className="text-ms-danger text-sm" data-testid="tour-edit-error">{error}</p>}
        <div className="flex justify-end gap-3 pt-4 border-t border-ms-edge">
          <button type="button" onClick={() => nav("/admin/tours")} className="btn-secondary">Cancel</button>
          <button disabled={saving} type="submit" className="btn-primary" data-testid="tour-save">{saving ? "Saving…" : "Save tour"}</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return <div><p className="label-eyebrow text-ms-stone text-xs mb-2">{label}</p>{children}</div>;
}
function Input({ label, value, onChange, type="text", required, step, testId, placeholder, emailHint }) {
  return (
    <label>
      <span className="label-eyebrow text-ms-stone text-xs">{label}</span>
      <input type={type} step={step} required={required} value={value ?? ""} onChange={e=>onChange(e.target.value)} data-testid={testId} placeholder={placeholder}
        className="mt-2 w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm focus:border-ms-brass outline-none" />
      {emailHint && <EmailHint />}
    </label>
  );
}

function EmailHint() {
  return (
    <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] tracking-[0.14em] uppercase text-[#7C5BA0] font-medium" data-testid="email-pull-hint">
      <span className="w-1 h-1 rounded-full bg-[#7C5BA0]" /> Appears in emails
    </span>
  );
}

// Restaurant stops editor — used by the Tour edit page for payout reports
function RestaurantStopsEditor({ stops, onChange }) {
  const list = Array.isArray(stops) ? stops : [];
  const apply = (next) => onChange(typeof next === "function" ? next : next);
  const add = () => apply(prev => {
    const src = Array.isArray(prev) ? prev : list;
    return [...src, { stop_number: src.length + 1, name: "", per_head_cost: 0, email: "", arrival_offset_minutes: src.length * 45 }];
  });
  const update = (i, patch) => apply(prev => (Array.isArray(prev) ? prev : list).map((s, j) => j === i ? { ...s, ...patch } : s));
  const remove = (i) => apply(prev => (Array.isArray(prev) ? prev : list).filter((_, j) => j !== i));

  return (
    <div data-testid="restaurant-stops-editor">
      <p className="text-xs text-ms-stone mb-3">Add each restaurant on this tour with its stop number, per-head food cost (for the Restaurant Payouts report), partner contact email (for the 24h-advance notification), and arrival offset in minutes from tour start (so the email tells them when guests will arrive).</p>
      <div className="space-y-3">
        {list.map((s, i) => (
          <div key={i} className="border border-ms-edge rounded-md p-3 space-y-2" data-testid={`rs-row-${i}`}>
            <div className="grid grid-cols-12 gap-2 items-center">
              <input
                type="number" min="1" max="20"
                value={s.stop_number ?? i + 1}
                onChange={e => update(i, { stop_number: Number(e.target.value) || 1 })}
                className="col-span-2 border border-ms-edge rounded-md px-2 py-2 text-sm"
                placeholder="#"
                data-testid={`rs-num-${i}`}
              />
              <input
                type="text"
                value={s.name || ""}
                onChange={e => update(i, { name: e.target.value })}
                placeholder="Restaurant name"
                className="col-span-7 border border-ms-edge rounded-md px-3 py-2 text-sm"
                data-testid={`rs-name-${i}`}
              />
              <div className="col-span-2 flex items-center gap-1">
                <span className="text-ms-stone">$</span>
                <input
                  type="number" min="0" step="0.01"
                  value={s.per_head_cost ?? 0}
                  onChange={e => update(i, { per_head_cost: Number(e.target.value) || 0 })}
                  placeholder="0.00"
                  className="w-full border border-ms-edge rounded-md px-2 py-2 text-sm"
                  data-testid={`rs-cost-${i}`}
                />
              </div>
              <button
                type="button"
                onClick={() => remove(i)}
                className="col-span-1 text-ms-danger hover:text-ms-danger/70"
                data-testid={`rs-remove-${i}`}
              >
                <Trash size={14} />
              </button>
            </div>
            <div className="grid grid-cols-12 gap-2 items-center">
              <label className="col-span-8 text-xs text-ms-stone">
                Restaurant contact email
                <input
                  type="email"
                  value={s.email || ""}
                  onChange={e => update(i, { email: e.target.value })}
                  placeholder="manager@restaurant.com"
                  className="mt-1 w-full border border-ms-edge rounded-md px-3 py-2 text-sm"
                  data-testid={`rs-email-${i}`}
                />
              </label>
              <label className="col-span-4 text-xs text-ms-stone">
                Arrival offset (min from tour start)
                <input
                  type="number" min="0" max="600" step="5"
                  value={s.arrival_offset_minutes ?? 0}
                  onChange={e => update(i, { arrival_offset_minutes: Number(e.target.value) || 0 })}
                  placeholder="0"
                  className="mt-1 w-full border border-ms-edge rounded-md px-3 py-2 text-sm"
                  data-testid={`rs-offset-${i}`}
                />
              </label>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-xs text-ms-stone bg-ms-cream/40 border border-dashed border-ms-edge rounded-md p-4">No restaurant stops added yet.</p>}
      </div>
      <button
        type="button"
        onClick={add}
        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-ms-edge rounded-md text-ms-stone hover:bg-ms-cream"
        data-testid="rs-add"
      >
        <Plus size={12} /> Add a stop
      </button>
    </div>
  );
}

function TextArea({ label, value, onChange, rows=3, emailHint }) {
  return (
    <label className="block">
      <span className="label-eyebrow text-ms-stone text-xs">{label}</span>
      <textarea rows={rows} value={value ?? ""} onChange={e=>onChange(e.target.value)} className="mt-2 w-full border border-ms-edge rounded-md px-3 py-2.5 text-sm focus:border-ms-brass outline-none" />
      {emailHint && <EmailHint />}
    </label>
  );
}
